import React, { useEffect, useState, useRef } from 'react';
import { Icon } from '@iconify/react';
interface Message {
  id: string;
  role: 'user' | 'ai';
  content: string;
}
interface AiChatProps {
  isOpen: boolean;
  onClose: () => void;
}
const examplePrompts = [{
  icon: 'tabler:chart-line',
  text: "Mostrami l'andamento delle candidature negli ultimi mesi"
}, {
  icon: 'tabler:chart-bar',
  text: 'Quali bandi hanno più candidature in valutazione?'
}, {
  icon: 'tabler:mail',
  text: 'Analizza le performance delle campagne email'
}, {
  icon: 'tabler:file-analytics',
  text: 'Genera un report sulle candidature vincitori'
}];
// Staggered animation helper
function staggerStyle(isAnimating: boolean, delayMs: number): React.CSSProperties {
  return {
    opacity: isAnimating ? 1 : 0,
    transform: isAnimating ? 'translateY(0)' : 'translateY(24px)',
    transition: `opacity 500ms ease ${delayMs}ms, transform 500ms cubic-bezier(0.16, 1, 0.3, 1) ${delayMs}ms`
  };
}
export function AiChat({
  isOpen,
  onClose
}: AiChatProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Handle open/close animation
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsAnimating(true));
      });
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => setIsVisible(false), 600);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 700);
    }
  }, [isOpen]);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    if (messages.length > 0) scrollToBottom();
  }, [messages]);
  const handleSend = (text?: string) => {
    const content = text || inputValue.trim();
    if (!content) return;
    const newUserMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content
    };
    setMessages(prev => [...prev, newUserMsg]);
    setInputValue('');
    setTimeout(() => {
      const newAiMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'ai',
        content: "Sto analizzando la tua richiesta. Questa è una risposta simulata dell'intelligenza artificiale. In un'implementazione reale, qui verrebbe mostrata l'analisi dei dati richiesti."
      };
      setMessages(prev => [...prev, newAiMsg]);
    }, 1200);
  };
  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    handleSend();
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  const hasMessages = messages.length > 0;
  if (!isVisible) return null;
  return <div className="fixed inset-0 z-[100] bg-white flex flex-col" style={{
    fontFamily: 'Roboto, sans-serif',
    opacity: isAnimating ? 1 : 0,
    transition: 'opacity 400ms ease'
  }} data-id="element-44">
      {/* Header */}
      <div className="flex items-center justify-between px-6 md:px-8 flex-shrink-0" style={{
      height: '64px',
      backgroundColor: '#FFFFFF',
      borderBottom: '1px solid #E4E4E4',
      ...staggerStyle(isAnimating, 50)
    }} data-id="element-45">
        <div className="flex items-center" data-id="element-46">
          <img src="https://dev.bandiperchicrea.it/images/logo_dark.svg" alt="Per Chi Crea" style={{
          height: '36px'
        }} data-id="element-47" />
        </div>
        <button onClick={onClose} className="flex items-center gap-1.5 px-3 py-1.5 rounded transition-colors" style={{
        color: '#80858E',
        fontSize: '14px',
        fontFamily: 'Roboto, sans-serif',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer'
      }} onMouseEnter={e => e.currentTarget.style.color = '#293B54'} onMouseLeave={e => e.currentTarget.style.color = '#80858E'} aria-label="Chiudi" data-id="element-48">
          <Icon icon="tabler:x" fontSize={18} data-id="element-49" />
          <span data-id="element-50">Esci</span>
        </button>
      </div>

      {/* Content Area */}
      {!hasMessages /* Welcome State */ ? <div className="flex-1 flex flex-col items-center justify-center px-6 overflow-y-auto" data-id="element-51">
          <div className="w-full max-w-[720px] mx-auto flex flex-col items-center" style={{
        marginTop: '-40px'
      }} data-id="element-52">
            {/* Sparkle Icon */}
            <div className="w-20 h-20 rounded-full flex items-center justify-center mb-6" style={{
          backgroundColor: '#EFF4F8',
          color: '#2A97DF',
          ...staggerStyle(isAnimating, 150)
        }} data-id="element-53">
              <Icon icon="tabler:sparkles" fontSize={40} data-id="element-54" />
            </div>

            {/* Heading */}
            <h1 className="text-center m-0 mb-2" style={{
          fontFamily: 'Roboto Condensed, sans-serif',
          fontSize: '28px',
          fontWeight: 400,
          color: '#293B54',
          ...staggerStyle(isAnimating, 250)
        }} data-id="element-55">
              Come posso aiutarti oggi?
            </h1>
            <p className="text-center m-0 mb-10" style={{
          fontSize: '15px',
          color: '#80858E',
          ...staggerStyle(isAnimating, 350)
        }} data-id="element-56">
              Chiedi analisi, visualizzazioni o suggerimenti strategici
            </p>

            {/* Input */}
            <form onSubmit={handleSubmit} className="w-full relative mb-10" style={staggerStyle(isAnimating, 450)} data-id="element-57">
              <textarea ref={inputRef} value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={handleKeyDown} placeholder="Fai una richiesta..." className="w-full resize-none outline-none transition-all" style={{
            height: '52px',
            padding: '14px 56px 14px 24px',
            fontSize: '15px',
            color: '#2A3A54',
            border: '1.5px solid #D0DCE8',
            borderRadius: '999px',
            backgroundColor: 'white',
            fontFamily: 'Roboto, sans-serif'
          }} rows={1} data-id="element-58" />
              <button type="submit" disabled={!inputValue.trim()} className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full flex items-center justify-center transition-colors" style={{
            width: '36px',
            height: '36px',
            backgroundColor: inputValue.trim() ? '#2A97DF' : 'transparent',
            color: inputValue.trim() ? 'white' : '#AAAAAA'
          }} data-id="element-59">
                <Icon icon="tabler:send" fontSize={18} data-id="element-60" />
              </button>
            </form>

            {/* Example Prompts */}
            <div className="w-full" style={staggerStyle(isAnimating, 550)} data-id="element-61">
              <p className="m-0 mb-4" style={{
            fontSize: '14px',
            fontWeight: 500,
            color: '#293B54',
            fontFamily: 'Roboto, sans-serif'
          }} data-id="element-62">
                Prova questi esempi:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-63">
                {examplePrompts.map((prompt, idx) => <button key={idx} type="button" onClick={() => handleSend(prompt.text)} className="flex items-center gap-3 text-left p-4 rounded-lg border border-[#E4E4E4] bg-white hover:border-[#2A97DF] hover:bg-[#FAFCFF] transition-all group" style={{
              cursor: 'pointer',
              ...staggerStyle(isAnimating, 600 + idx * 80)
            }} data-id="element-64">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{
                backgroundColor: '#EFF4F8',
                color: '#2A97DF'
              }} data-id="element-65">
                      <Icon icon={prompt.icon} fontSize={18} data-id="element-66" />
                    </div>
                    <span style={{
                fontSize: '14px',
                color: '#444444',
                lineHeight: 1.4,
                fontFamily: 'Roboto, sans-serif'
              }} data-id="element-67">
                      {prompt.text}
                    </span>
                  </button>)}
              </div>
            </div>
          </div>
        </div> /* Chat State */ : <>
          <div className="flex-1 overflow-y-auto py-6 px-6 bg-white" data-id="element-68">
            <div className="max-w-[720px] mx-auto space-y-5" data-id="element-69">
              {messages.map(msg => <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-70">
                  {msg.role === 'ai' && <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mr-3 mt-1" style={{
              backgroundColor: '#EFF4F8',
              color: '#2A97DF'
            }} data-id="element-71">
                      <Icon icon="tabler:sparkles" fontSize={16} data-id="element-72" />
                    </div>}
                  <div className={`max-w-[75%] px-4 py-3 ${msg.role === 'user' ? 'bg-[#293B54] text-white rounded-2xl rounded-tr-md' : 'bg-[#F5F5F5] text-[#444444] rounded-2xl rounded-tl-md'}`} style={{
              fontSize: '14px',
              lineHeight: '1.6'
            }} data-id="element-73">
                    {msg.content}
                  </div>
                </div>)}
              <div ref={messagesEndRef} data-id="element-74" />
            </div>
          </div>

          {/* Chat Input */}
          <div className="flex-shrink-0 bg-white px-6 py-4" style={{
        borderTop: '1px solid #E4E4E4'
      }} data-id="element-75">
            <div className="max-w-[720px] mx-auto relative" data-id="element-76">
              <form onSubmit={handleSubmit} className="relative" data-id="element-77">
                <textarea ref={inputRef} value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={handleKeyDown} placeholder="Scrivi un messaggio..." className="w-full resize-none outline-none transition-all" style={{
              height: '48px',
              padding: '12px 56px 12px 24px',
              fontSize: '14px',
              color: '#2A3A54',
              border: '1.5px solid #D0DCE8',
              borderRadius: '999px',
              backgroundColor: 'white',
              fontFamily: 'Roboto, sans-serif'
            }} rows={1} data-id="element-78" />
                <button type="submit" disabled={!inputValue.trim()} className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full flex items-center justify-center transition-colors" style={{
              width: '34px',
              height: '34px',
              backgroundColor: inputValue.trim() ? '#2A97DF' : 'transparent',
              color: inputValue.trim() ? 'white' : '#AAAAAA'
            }} data-id="element-79">
                  <Icon icon="tabler:send" fontSize={18} data-id="element-80" />
                </button>
              </form>
              <div className="text-center mt-2" data-id="element-81">
                <span style={{
              fontSize: '11px',
              color: '#AAAAAA'
            }} data-id="element-82">
                  L'AI può commettere errori. Verifica sempre le informazioni
                  importanti.
                </span>
              </div>
            </div>
          </div>
        </>}
    </div>;
}